/**
 * RealPage Calendar Block
 *
 * Injection block that loads the RealPage appointment scheduler widget.
 * RealPage renders the floating launcher itself; clicking it opens the
 * scheduler calendar.
 */

import { useEffect, useRef } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';
import { useExternalScript } from '../../hooks/useExternalScript';

const CALENDAR_SCRIPT_SRC = 'https://cs-cdn.realpage.com/CAL/v1.0/cs-calendar-widget.min.js';
const CALENDAR_ID_PATTERN = /new\s+csCalendar\s*\(\s*(?:"([^"]+)"|'([^']+)')\s*\)/i;
const SCRIPT_SNIPPET_PATTERN = /<script[\s>]|csCalendar/i;
const VENDOR_NODE_IDS = [
  'cs-calendar',
  'cs-calendar-button',
  'cs-calendar-iframe',
  'cs-chat-calendar-wrapper',
];

type RealpageCalendarInstance = {
  close?: () => void;
};

type RealpageCalendarConstructor = new (calendarId: string) => RealpageCalendarInstance;

function normalize(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function extractCalendarId(value: string | null | undefined) {
  const normalized = normalize(value);
  if (!normalized) return null;

  const match = normalized.match(CALENDAR_ID_PATTERN);
  const calendarId = normalize(match?.[1] ?? match?.[2]);
  if (calendarId) return calendarId;

  if (SCRIPT_SNIPPET_PATTERN.test(normalized)) return null;
  return normalized;
}

function getCalendarConstructor() {
  const win = window as Window & { csCalendar?: RealpageCalendarConstructor };
  return win.csCalendar ?? null;
}

function removeVendorNodes() {
  VENDOR_NODE_IDS.forEach(id => {
    document.getElementById(id)?.remove();
  });
}

function RealpageCalendarComponent(_props: BlockProps) {
  const calendarId = extractCalendarId(
    useBlockToken('calendar_id', { defaultField: 'custom_realpage_tour_widget' }),
  );
  const scriptStatus = useExternalScript(calendarId ? CALENDAR_SCRIPT_SRC : null);
  const instanceRef = useRef<RealpageCalendarInstance | null>(null);

  useEffect(() => {
    if (!calendarId || scriptStatus !== 'ready') return;

    const Calendar = getCalendarConstructor();
    if (!Calendar) {
      console.error('RealPage calendar script loaded, but csCalendar was not available.');
      return;
    }

    removeVendorNodes();

    try {
      instanceRef.current = new Calendar(calendarId);
    } catch (error) {
      console.error('Failed to initialize RealPage calendar', error);
    }

    return () => {
      instanceRef.current?.close?.();
      instanceRef.current = null;
      removeVendorNodes();
    };
  }, [calendarId, scriptStatus]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'RealpageCalendar',
  displayName: 'RealPage Calendar',
  tokens: {
    calendar_id: {
      label: 'RealPage Calendar Script or ID',
      type: 'text',
      defaultField: 'custom_realpage_tour_widget',
      suggestedFields: ['custom_realpage_tour_widget'],
      description:
        'RealPage appointment scheduler embed snippet or property ID. Script snippets are parsed for the new csCalendar("...") value.',
      required: true,
    },
  },
};

export const realpageCalendarBlock: BlockDefinition = {
  id: 'realpage-calendar',
  name: 'RealPage Calendar',
  description:
    'Floating RealPage appointment scheduler widget. The launcher appears on the page and opens the calendar flow.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: RealpageCalendarComponent,
  contentSchema,
  tags: [
    'realpage',
    'calendar',
    'chatbot',
    'widget',
    'appointment',
    'tour',
    'schedule',
    'scheduler',
    'leasing',
  ],
};
